import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Payee / counterparty picker ──────────────────────────────────────────────
// Type-to-search over the firm-wide partner master. Picking one fills name, tax ID and ADDRESS in a
// single patch — the address in particular, because Form 50 Tawi prints it and it was blank on every
// manually entered line before the partner database existed.
//
// Free typing is preserved on purpose: plenty of existing payees are in no registry, and forcing a
// selection would block entry. What is typed is kept; the dropdown is an offer, not a gate. When the
// typed name matches nothing, the last row offers to save it as a new partner so the list grows from
// real work instead of a separate data-entry chore.

export interface PartnerPick {
  partnerId: string; name: string; taxId: string; address: string; entityType: 'individual' | 'company';
  branchNo: string;                    // '00000' = head office; VAT lines split this into head/branch
}

export default function PartnerPicker({ value, clientId, onPick, onCommit, placeholder, style, inputStyle }: {
  value: string;                       // the stored name; the field seeds from it and resyncs when it changes
  clientId: string;
  onPick: (p: PartnerPick) => void;    // a master row was selected — write every field at once
  onCommit: (name: string) => void;    // free-typed name, on blur, only when it actually changed
  placeholder?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;    // merged into the input — the VAT grid uses it for its missing-field highlight
}) {
  const { t } = useTranslation();
  // The input owns its text so the parent needs no state for it; `value` reseeds it when the row's
  // stored name changes underneath (reload, partner pick, another edit).
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timer = useRef<any>(null);

  // Debounced so a fast typist does not fire a query per keystroke.
  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.partnerSearch(text || '', 12).then((r: any) => setRows(r.rows || [])).catch(() => setRows([]));
    }, 180);
    return () => clearTimeout(timer.current);
  }, [text, open]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const choose = (r: any) => {
    setText(r.name || '');
    onPick({
      partnerId: r.id, name: r.name || '', taxId: r.tax_id || '', address: r.address || '',
      entityType: r.entity_type === 'individual' ? 'individual' : 'company',
      branchNo: String(r.branch_no ?? '') || '00000',
    });
    setOpen(false);
  };

  // Save whatever was typed as a new partner, linked to this client, and select it.
  const createFromTyped = async () => {
    const name = (text || '').trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const r: any = await api.partnerCreate({ name, clientId });
      choose({ id: r.id, name, tax_id: '', address: '', entity_type: 'company', branch_no: '00000' });
    } catch { /* leave the typed text alone */ } finally { setBusy(false); }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative', ...style }}>
      <input
        ref={inputRef}
        value={text}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={e => { setText(e.target.value); setOpen(true); }}
        // 120ms so a click on a dropdown row lands before the blur commit fires.
        onBlur={() => setTimeout(() => { if ((text || '') !== (value || '')) onCommit(text); }, 120)}
        style={{ width: '100%', padding: '5px 7px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box', ...inputStyle }}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, minWidth: 280, marginTop: 3, zIndex: 60, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', maxHeight: 260, overflowY: 'auto' }}>
          {rows.map(r => (
            <button key={r.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => choose(r)}
              style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--border)', background: 'transparent', padding: '7px 10px', cursor: 'pointer' }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                {r.name}
                {/* OCR/web-seeded suggestions are UNVERIFIED — visibly so, or the backfill's ~200
                    placeholder names would be indistinguishable from human-confirmed ones. */}
                {r.verified === 0 && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, padding: '0px 5px', borderRadius: 4, background: '#FEF3F2', color: '#B42318' }}>UNVERIFIED</span>}
              </div>
              <div style={{ fontSize: 11, color: r.address ? 'var(--text-secondary)' : '#B45309' }}>
                {r.tax_id || t('pt_no_tax')}
                {r.address ? ` · ${String(r.address).slice(0, 46)}` : ` · ${t('pt_no_address')}`}
              </div>
            </button>
          ))}
          {(text || '').trim() && (
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={createFromTyped} disabled={busy}
              style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '7px 10px', cursor: busy ? 'default' : 'pointer', fontSize: 12, color: 'var(--accent)' }}>
              <i className={`ti ${busy ? 'ti-loader-2' : 'ti-plus'}`} style={{ marginRight: 5, animation: busy ? 'spin .7s linear infinite' : undefined, display: 'inline-block' }} />
              {t('pt_create_typed').replace('{n}', (text || '').trim())}
            </button>
          )}
          {!rows.length && !(text || '').trim() && (
            <div style={{ padding: '9px 10px', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pt_search_hint')}</div>
          )}
        </div>
      )}
    </div>
  );
}
